import { IPoint2d } from '@/editor/math/IPoint2d';
import { EOperateMode, EToolType } from '@/editor/types';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { clientPosToWorldPos } from '@/editor/utils';
import { CommonController2d } from '../CommonController';
import VirtualArcWall from '@/editor/entities/2d/virtualArcWall';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import { MAX_CHORD_LENGTH, MAX_SAGITTA, MIN_CHORD_LENGTH, MIN_SAGITTA } from '@/editor/constant/wall';
import VirtualWallModel from '@/editor/entities/2d/virtualWallModel';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { ESepcialKey } from '@/editor/stores/keyboardStore';
import { FloatingPoint } from '@/editor/math/number';

enum EDrawArcWallStage {
  Start = 1, // 绘制起始点
  End = 2, // 绘制结束点
  Sagitta = 3 // 绘制弦高
}

interface IAddWallPoint {
  stage: EDrawArcWallStage.End | EDrawArcWallStage.Start;
  point: IPoint2d;
}

interface IAddWallSagitta {
  stage: EDrawArcWallStage.Sagitta;
  sagitta: number;
}

type TAddWallPoint = IAddWallPoint | IAddWallSagitta;

class DrawArcWall extends CommonController2d {
  protected _movePoint?: IPoint2d | null;

  protected _startPoint?: IPoint2d | null;
  protected _endPoint?: IPoint2d | null;
  protected _sagitta?: number | null;
  private stage: EDrawArcWallStage = EDrawArcWallStage.Start; // 记录当前在哪个绘制阶段

  private virtualArcWall = new VirtualArcWall();
  private virtualStraightWall = new VirtualWallModel();
  private virtualRuler: RulerModel;

  private _absorbManager = new WallPointAbsorbManager(globalStores.wallStore);
  private _eventDispose: () => void;

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this.virtualRuler = new RulerModel({ onCommit: this.handleRulerChange.bind(this) });
    this._absorbManager.updateWallData();

    this._eventDispose = editorEvent.on(EditorEventName.KEYBOARD_KEY_PRESS, (key: string) => {
      if (key === ESepcialKey.Escape) {
        if (this.stage === EDrawArcWallStage.Start) {
          this._gs.operateInfo = { mode: EOperateMode.EMPTY, toolType: EToolType.empty };
        } else {
          this.clear();
          this.stage = EDrawArcWallStage.Start;
        }
      }
    });
  }

  protected async addWallPoint(params: TAddWallPoint) {
    switch (params.stage) {
      case EDrawArcWallStage.Start:
        this._startPoint = params.point;
        this.stage = EDrawArcWallStage.End;
        this._absorbManager.setOrthogonalData(params.point);
        break;
      case EDrawArcWallStage.End: {
        if (!this._startPoint) return;
        const chordLength = Point2dUtils.distanceTo(this._startPoint, params.point);
        if (FloatingPoint.lessThan(chordLength, MIN_CHORD_LENGTH)) return;

        if (FloatingPoint.greaterThanOrEqual(chordLength, MAX_CHORD_LENGTH)) {
          this._endPoint = Point2dUtils.addDistance(
            this._startPoint,
            MAX_CHORD_LENGTH,
            Point2dUtils.getDirection(this._startPoint, params.point)
          );
          this._sagitta = MAX_CHORD_LENGTH / 2;
        } else {
          this._endPoint = params.point;
          this._sagitta = chordLength / 2;
        }

        this._movePoint = null;

        this.stage = EDrawArcWallStage.Sagitta;
        this._absorbManager.clearModel();
        break;
      }

      case EDrawArcWallStage.Sagitta: {
        if (!this._startPoint || !this._endPoint || FloatingPoint.lessThan(Math.abs(params.sagitta), MIN_SAGITTA))
          return;

        if (FloatingPoint.greaterThanOrEqual(Math.abs(params.sagitta), MAX_SAGITTA)) {
          this._sagitta = MAX_SAGITTA * Math.sign(params.sagitta);
        } else {
          this._sagitta = params.sagitta;
        }

        const arc = new ArcSeg2d({ start: this._startPoint, end: this._endPoint, sagitta: this._sagitta });
        const isAddSuccess = await this._gs.wallOperation.addWall({
          start: this._startPoint,
          end: this._endPoint,
          center: arc.center,
          clockwise: arc.clockwise,
          operateMode: EOperateMode.DrawArcWall
        });

        if (!isAddSuccess) return;

        const nextStartPoint = this._endPoint;
        this.clear();

        this._absorbManager.updateWallData();
        this.stage = EDrawArcWallStage.Start;
        void this.addWallPoint({ stage: EDrawArcWallStage.Start, point: nextStartPoint }); // 连续绘制

        break;
      }
    }

    this.updateVirtualModel();
  }

  // 开始/结束绘制墙体时会有很多状态设定
  protected override mouseUp(e: MouseEvent, isDragged: boolean) {
    if (isDragged) return;

    if (e.button === 2) {
      this.backward();
      return;
    }

    if (e.button !== 0) return;

    switch (this.stage) {
      case EDrawArcWallStage.Start:
      case EDrawArcWallStage.End: {
        if (!this._movePoint) break;
        void this.addWallPoint({ point: this._movePoint, stage: this.stage });
        break;
      }
      case EDrawArcWallStage.Sagitta: {
        if (!this._sagitta) break;
        void this.addWallPoint({ sagitta: this._sagitta, stage: this.stage });
        break;
      }
    }
  }

  private backward() {
    switch (this.stage) {
      case EDrawArcWallStage.Start:
        // 退出编辑
        this.dispose();
        this.exit();
        break;
      case EDrawArcWallStage.End:
        this.clear();
        this.stage = EDrawArcWallStage.Start;
        break;
      case EDrawArcWallStage.Sagitta:
        this.clear();
        this.stage = EDrawArcWallStage.Start;
        break;
    }
  }

  protected override mouseMove(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    let point = clientPosToWorldPos(e, camera, renderer);

    if (this.stage === EDrawArcWallStage.Sagitta) {
      if (!this._startPoint || !this._endPoint) {
        return;
      }
      const absorbPoint = this._absorbManager.calculateArcAbsorbPointThenRender({
        arcStart: this._startPoint,
        arcEnd: this._endPoint,
        rawPoint: point
      });

      if (absorbPoint) point = absorbPoint;

      let sagitta = ArcSeg2d.calculateSagitta(this._startPoint, this._endPoint, point);
      if (Math.abs(sagitta) < MIN_SAGITTA) {
        sagitta = MIN_SAGITTA * Math.sign(sagitta);
        this._absorbManager.clearModel();
      } else if (Math.abs(sagitta) > MAX_SAGITTA) {
        sagitta = MAX_SAGITTA * Math.sign(sagitta);
        this._absorbManager.clearModel();
      }

      this._sagitta = sagitta;
      this._movePoint = null;
    } else {
      const absorbPoint = this._absorbManager.calculateAbsorbPointThenRender(point);
      if (absorbPoint) {
        point = absorbPoint;
      }

      if (this.stage === EDrawArcWallStage.End && this._startPoint) {
        const chordLength = Point2dUtils.distanceTo(this._startPoint, point);
        if (FloatingPoint.greaterThan(chordLength, MAX_CHORD_LENGTH)) {
          point = Point2dUtils.addDistance(
            this._startPoint,
            MAX_CHORD_LENGTH,
            Point2dUtils.getDirection(this._startPoint, point)
          );
          this._absorbManager.clearModel();
        }
      }

      this._movePoint = point;
      this._sagitta = null;
    }

    this.updateVirtualModel();
  }

  private updateVirtualModel() {
    switch (this.stage) {
      case EDrawArcWallStage.Start:
        break;
      case EDrawArcWallStage.End:
        if (this._startPoint && this._movePoint) {
          this.virtualArcWall.clear();
          this.virtualStraightWall.update([
            {
              start: this._startPoint,
              end: this._movePoint,
              thickness: this._gs.paramStore.wallThickness
            }
          ]);

          this.virtualRuler.resetFocusId();
          this.virtualRuler.updateModel(
            [
              {
                id: '1',
                start: this._startPoint,
                end: this._movePoint,
                editable: true,
                focusTrap: true,
                max: MAX_CHORD_LENGTH
              }
            ],
            '1'
          );
        }
        break;
      case EDrawArcWallStage.Sagitta:
        if (this._startPoint && this._endPoint && this._sagitta) {
          // 知道起点、终点、线上点，计算弦高
          const arc = new ArcSeg2d({
            start: this._startPoint,
            end: this._endPoint,
            sagitta: this._sagitta
          });

          this.virtualStraightWall.clear();
          this.virtualArcWall.update({
            start: this._startPoint,
            end: this._endPoint,
            thickness: this._gs.paramStore.wallThickness,
            center: arc.center,
            clockwise: arc.clockwise
          });
          this.virtualRuler.resetFocusId();
          this.virtualRuler.updateModel(
            [
              { id: '1', start: this._startPoint, end: this._endPoint, offset: 0 },
              {
                id: '2',
                start: this._startPoint,
                end: this._endPoint,
                center: arc.center,
                clockwise: arc.clockwise,
                editable: true,
                focusTrap: true,
                max: MAX_SAGITTA
              }
            ],
            '2'
          );
        }
        break;
    }
  }

  handleRulerChange(id: string, value: number) {
    switch (this.stage) {
      case EDrawArcWallStage.Start:
        break;
      case EDrawArcWallStage.End: {
        if (!this._movePoint || !this._startPoint) break;
        const direction = Point2dUtils.subtracted(this._movePoint, this._startPoint);
        const newEndPoint = Point2dUtils.addDistance(this._startPoint, value, direction);
        void this.addWallPoint({ point: newEndPoint, stage: this.stage });
        break;
      }
      case EDrawArcWallStage.Sagitta: {
        const sagitta = Math.sign(this._sagitta || 1) * value;
        void this.addWallPoint({ sagitta, stage: this.stage });
        break;
      }
    }
  }

  clear() {
    this.virtualArcWall.clear();
    this.virtualStraightWall.clear();
    this.virtualRuler.clear();
    this._absorbManager.clearOrthogonalData();
    this._absorbManager.clearModel();

    this._movePoint = null;
    this._startPoint = null;
    this._endPoint = null;
    this._sagitta = null;
  }

  override dispose() {
    this.clear();
    this._eventDispose();
    this._absorbManager.dispose();
    this.virtualArcWall.dispose();
    this.virtualStraightWall.dispose();
    this.virtualRuler.dispose();
    super.dispose();
  }
}

export default DrawArcWall;
